import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Tooltip,
    useMediaQuery,
    useTheme
} from "@mui/material";
import Typography from "@mui/material/Typography";
import type {AggregationSurveyEvaluationAnswerRow}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import React, {useContext, useState} from "react";
import {useTranslation} from "react-i18next";
import {useInfiniteQuery} from "react-query";
import {directionColor, signed, useInsightsFormatters} from "../../insights/kit/insightsFormat";
import {SurveyAnswerContext, surveyQuestionTitle} from "../shared/surveyAnswer.util";
import {SurveyRequiredMarker} from "../shared/SurveyQuestionMarkers";
import {getSurveyQuestionTypeFacts} from "../shared/surveyQuestionTypeFacts";
import {SURVEY_EVALUATION_QUERY_KEY} from "../shared/surveyQueryRefresh";
import {buildSurveyEvaluationRequest, SurveyEvaluationScopeContext} from "../shared/useSurveyEvaluation";
import SurveyEvaluationSubmissionDialog from "./SurveyEvaluationSubmissionDialog";
import {evaluationBuckets} from "./surveyEvaluation.util";
import {isCommentQuestion} from "./questionSummary.util";
import SurveyEvaluationCommentRating from "./SurveyEvaluationCommentRating";

const PAGE_SIZE = 25;

const TITLE_ID = 'survey-evaluation-answers-title';

export interface SurveyEvaluationAnswersBucket {
    key: string
    label: string
}

interface Props {
    question: SurveyQuestion
    number: number
    /** One answer bar, or everyone who answered the question when omitted. */
    bucket?: SurveyEvaluationAnswersBucket
    /** The overview result these individual answers explain; bucket dialogs already name their selected answer. */
    summary?: {score: number | null; trend: number | null}
    ctx: SurveyAnswerContext
    /** Explicit sensitive-text opt-in; the API logs released text. */
    includeSensitive: boolean
    onClose: () => void
}

type SortField = 'submittedAt' | 'profileName' | 'answerValue' | 'commentScore';

interface AnswersSort {
    field: SortField
    direction: 'ASC' | 'DESC'
}

// Dates newest first, names A to Z, rated answers lowest first.
const FIRST_DIRECTION: Record<SortField, AnswersSort['direction']> = {
    submittedAt: 'DESC', profileName: 'ASC', answerValue: 'ASC', commentScore: 'ASC',
};

interface SortHeaderProps {
    label: string
    field: SortField
    sort: AnswersSort
    onSort: (field: SortField) => void
}

function SortHeader({label, field, sort, onSort}: SortHeaderProps) {
    const active = sort.field === field;
    const Arrow = active && sort.direction === 'ASC' ? ArrowUpwardRoundedIcon : ArrowDownwardRoundedIcon;

    return (
        <Box role={'columnheader'} aria-sort={active ? (sort.direction === 'ASC' ? 'ascending' : 'descending') : 'none'} sx={{textAlign: field === 'answerValue' ? 'right' : 'left'}}>
            <ButtonBase
                onClick={() => onSort(field)}
                sx={{
                    gap: 0.5,
                    font: 'inherit',
                    color: active ? 'text.primary' : 'inherit',
                    borderRadius: 0.5,
                    '& .sort-arrow': {opacity: active ? 1 : 0},
                    '&:hover .sort-arrow, &:focus-visible .sort-arrow': {opacity: active ? 1 : 0.5},
                }}
            >
                {label}
                <Arrow className={'sort-arrow'} sx={{fontSize: 16}}/>
            </ButtonBase>
        </Box>
    );
}

/**
 * Who answered this question, optionally narrowed to one answer, with written text only from this question.
 * The full reply is available separately.
 */
function SurveyEvaluationAnswersDialog({
    question,
    number,
    bucket,
    summary,
    ctx,
    includeSensitive,
    onClose
}: Props) {
    const {t} = useTranslation();
    const theme = useTheme();
    const {whole: formatWhole} = useInsightsFormatters();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const scope = useContext(SurveyEvaluationScopeContext);

    const surveyId = scope?.surveyId;
    const questionId = question.id;
    const facts = getSurveyQuestionTypeFacts(question.type);
    const showCommentRating = isCommentQuestion(question) && !question.isSensitive;

    const [sort, setSort] = useState<AnswersSort>(() => {
        if (!bucket && facts?.answer === 'number') return {field: 'answerValue', direction: 'ASC'};
        if (showCommentRating) return {field: 'commentScore', direction: 'ASC'};
        return {field: 'submittedAt', direction: 'DESC'};
    });
    const changeSort = (field: SortField) => setSort(current => current.field === field
        ? {field, direction: current.direction === 'ASC' ? 'DESC' : 'ASC'}
        : {field, direction: FIRST_DIRECTION[field]});

    const answers = useInfiniteQuery(
        [SURVEY_EVALUATION_QUERY_KEY, 'answers', surveyId, scope?.filters, questionId, bucket?.key, includeSensitive, sort],
        ({pageParam = 0}) => SurveyAggregationService.listEvaluationAnswers({
            evaluation: buildSurveyEvaluationRequest(surveyId!, scope!.filters, includeSensitive),
            questionId: questionId!,
            value: bucket?.key,
            limit: {limit: PAGE_SIZE, offset: pageParam, sort: [sort]},
        }),
        {
            enabled: !!surveyId && !!questionId && !!scope,
            refetchOnWindowFocus: false,
            getNextPageParam: (last, pages) => {
                const loaded = pages.reduce((sum, page) => sum + page.content.length, 0);
                return loaded < last.totalElements ? loaded : undefined;
            },
        }
    );
    const rows = answers.data?.pages.flatMap(page => page.content) ?? [];
    const totalAnswers = answers.data?.pages[0]?.totalElements;
    const hasWrittenAnswers = rows.some(row => row.answerTexts?.length);
    const showValues = !bucket && facts != null && ['number', 'boolean', 'options', 'date'].includes(facts.answer);
    const hasAnswerColumn = showValues || hasWrittenAnswers;
    const numericAnswer = showValues && facts?.answer === 'number';
    const valueLabels = new Map(evaluationBuckets(
        question,
        ctx,
        Object.fromEntries(rows.flatMap(row => row.values ?? []).map(value => [value, 1]))
    ).map(value => [value.key, facts?.scale ? `${value.label} / ${facts.scale.max}` : value.label]));
    const rowColumns = {
        xs: 'minmax(0, 1fr) 40px',
        sm: hasAnswerColumn
            ? `104px minmax(120px, 1fr) ${numericAnswer && !hasWrittenAnswers ? '88px' : 'minmax(0, 2fr)'} 40px`
            : '104px minmax(0, 1fr) 40px',
    };
    const answerLabel = bucket && (facts?.scale ? `${bucket.label} / ${facts.scale.max}` : bucket.label);
    const answerCount = totalAnswers == null ? t('Answers') : t('Answer count', {count: totalAnswers});
    const currentRange = scope?.filters.range;
    const period = currentRange && `${formatFullDMY(currentRange.start)} – ${formatFullDMY(currentRange.end)}`;
    const trend = summary?.trend == null ? null : signed(summary.trend, formatWhole);

    // Under the evaluation's key, so resolving the reply refreshes it together with the tab.
    const [submissionId, setSubmissionId] = useState<string>();

    const guestName = (row: AggregationSurveyEvaluationAnswerRow) => (
        row.profileName?.trim()
            || (row.profileId ? t('Unknown guest') : t('Anonymous guest'))
    );

    const body = () => {
        if (answers.isLoading) {
            return (
                <Stack alignItems={'center'} sx={{py: 6}}>
                    <CircularProgress size={24}/>
                </Stack>
            );
        }

        if (answers.isError) {
            return (
                <Stack alignItems={'center'} gap={1.5} sx={{py: 6}}>
                    <Typography variant={'body1'} color={'text.secondary'}>
                        {t('Could not load the answers.')}
                    </Typography>
                    <Button onClick={() => answers.refetch()}>{t('Try again')}</Button>
                </Stack>
            );
        }

        if (!rows.length) {
            return (
                <Typography variant={'body1'} color={'text.secondary'} sx={{py: 6, textAlign: 'center'}}>
                    {t('No answers in this period.')}
                </Typography>
            );
        }

        return (
            <Box role={'table'} aria-labelledby={TITLE_ID}>
                <Box
                    role={'row'}
                    sx={{
                        display: {xs: 'none', sm: 'grid'},
                        gridTemplateColumns: rowColumns,
                        gap: 2,
                        px: 1.5,
                        py: 1,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '& > *': {fontSize: 14, fontWeight: 600, color: 'text.secondary'},
                    }}
                >
                    <SortHeader label={t('Date')} field={'submittedAt'} sort={sort} onSort={changeSort}/>
                    <SortHeader label={t('Guest')} field={'profileName'} sort={sort} onSort={changeSort}/>
                    {hasAnswerColumn && (
                        numericAnswer
                            ? <SortHeader label={t('Answer')} field={'answerValue'} sort={sort} onSort={changeSort}/>
                            : showCommentRating
                                ? <SortHeader label={`${t('Written answer')} · ${t('Score')}`} field={'commentScore'} sort={sort} onSort={changeSort}/>
                                : <Box role={'columnheader'}>{t(showValues ? 'Answer' : 'Written answer')}</Box>
                    )}
                    <Box role={'columnheader'} aria-label={t('Actions')}/>
                </Box>

                {rows.map(row => {
                    const known = !!row.profileName?.trim();
                    const values = showValues ? (row.values ?? []).map(value => valueLabels.get(value) ?? value).join(', ') : '';

                    return (
                        <Box
                            key={row.submissionId}
                            role={'row'}
                            onClick={() => {
                                if (window.getSelection()?.toString().trim()) return;
                                setSubmissionId(row.submissionId);
                            }}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: rowColumns,
                                columnGap: 2,
                                rowGap: 0.5,
                                alignItems: hasWrittenAnswers ? 'start' : 'center',
                                px: 1.5,
                                py: 1.25,
                                cursor: 'pointer',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:last-of-type': {borderBottom: 'none'},
                                '&:hover': {bgcolor: 'action.hover'},
                            }}
                        >
                            <Typography
                                role={'cell'}
                                variant={'body1'}
                                color={'text.secondary'}
                                sx={{fontVariantNumeric: 'tabular-nums', gridColumn: {xs: 1, sm: 'auto'}}}
                            >
                                {row.submittedAt ? formatFullDMY(new Date(row.submittedAt)) : '—'}
                            </Typography>

                            <Typography
                                role={'cell'}
                                variant={'body1'}
                                color={known ? 'text.primary' : 'text.secondary'}
                                sx={{fontWeight: known ? 600 : 400, overflowWrap: 'anywhere', gridColumn: {xs: 1, sm: 'auto'}}}
                            >
                                {guestName(row)}
                            </Typography>

                            {hasAnswerColumn && (
                                <Stack role={'cell'} gap={0.5} sx={{minWidth: 0, gridColumn: {xs: 1, sm: 'auto'}, textAlign: {sm: numericAnswer ? 'right' : 'left'}}}>
                                    {values && (
                                        <Typography variant={'body1'} sx={{fontWeight: 600, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere', textAlign: {sm: numericAnswer ? 'right' : 'left'}}}>
                                            {values}
                                        </Typography>
                                    )}
                                    {row.answerTexts?.map((text, position) => (
                                        <Typography
                                            key={position}
                                            variant={'body1'}
                                            sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}
                                        >
                                            {text}
                                        </Typography>
                                    ))}
                                    {!values && !row.answerTexts?.length && <Typography variant={'body1'} color={'text.disabled'}>—</Typography>}
                                    {showCommentRating && <SurveyEvaluationCommentRating sentiment={row.sentiment} score={row.commentScore}/>}
                                </Stack>
                            )}

                            <Box
                                role={'cell'}
                                sx={{gridColumn: {xs: 2, sm: 'auto'}, gridRow: {xs: '1 / span 2', sm: 'auto'}}}
                            >
                                <Tooltip title={t('View submission')}>
                                    <IconButton
                                        size={'small'}
                                        aria-label={`${t('View submission')}: ${guestName(row)}`}
                                        onClick={event => {
                                            event.stopPropagation();
                                            setSubmissionId(row.submissionId);
                                        }}
                                    >
                                        <VisibilityOutlinedIcon fontSize={'small'}/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    );
                })}

                {answers.hasNextPage && (
                    <Stack alignItems={'center'} sx={{py: 2}}>
                        <Button
                            variant={'outlined'}
                            onClick={() => answers.fetchNextPage()}
                            disabled={answers.isFetchingNextPage}
                            startIcon={answers.isFetchingNextPage ? <CircularProgress size={16} color={'inherit'}/> : undefined}
                        >
                            {t('Load more')}
                        </Button>
                    </Stack>
                )}
            </Box>
        );
    };

    return (
        <React.Fragment>
            <Dialog
                open
                onClose={onClose}
                fullWidth
                maxWidth={hasWrittenAnswers || (!bucket && facts?.answer === 'text') ? 'md' : 'sm'}
                fullScreen={fullScreen}
                aria-labelledby={TITLE_ID}
                PaperProps={{sx: {maxHeight: fullScreen ? '100%' : 'min(80vh, 720px)', overflow: 'hidden'}}}
            >
                <Stack direction={'row'} alignItems={'flex-start'} gap={1} sx={{px: 3, pt: 2, pb: 1.5, flexShrink: 0}}>
                    <Stack gap={0.25} sx={{flex: 1, minWidth: 0}}>
                        <Typography id={TITLE_ID} component={'h2'} variant={'h6'} sx={{overflowWrap: 'anywhere'}}>
                            {`${number}. ${surveyQuestionTitle(question, ctx)}`}
                            {question.isRequired && (
                                <Box component={'span'} sx={{ml: 0.5}}><SurveyRequiredMarker t={ctx.t}/></Box>
                            )}
                        </Typography>

                        <Typography variant={'body2'} color={'text.secondary'} sx={{overflowWrap: 'anywhere'}}>
                            {bucket ? `${t('Answer')}: ${answerLabel}` : answerCount}
                            {bucket && totalAnswers != null && ` · ${answerCount}`}
                            {!bucket && period && ` · ${period}`}
                        </Typography>

                        {summary && (summary.score != null || trend != null) && (
                            <Stack direction={'row'} gap={2} flexWrap={'wrap'} sx={{mt: 0.75}}>
                                {summary.score != null && (
                                    <Typography variant={'body2'} sx={{fontWeight: 600}}>
                                        {`${t('Score')}: ${formatWhole(summary.score)} / 100`}
                                    </Typography>
                                )}
                                {trend != null && (
                                    <Typography variant={'body2'} sx={{fontWeight: 600, color: directionColor(trend.direction)}}>
                                        {`${t('Trend')}: ${trend.text}`}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Stack>

                    <IconButton aria-label={t('Close')} onClick={onClose} sx={{mr: -1}}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>

                {/* Inset by half the header's padding: the rows' hover reaches past the text, not to the dialog edge. */}
                <DialogContent sx={{px: 1.5, pt: 0, pb: 2, minHeight: 0, overflowY: 'auto'}}>
                    {body()}
                </DialogContent>
            </Dialog>

            {!!submissionId && (
                <SurveyEvaluationSubmissionDialog
                    submissionId={submissionId}
                    questionId={questionId!}
                    onClose={() => setSubmissionId(undefined)}
                />
            )}
        </React.Fragment>
    );
}

export default SurveyEvaluationAnswersDialog;
